import type { ChangeEventHandler, ComponentProps, FocusEventHandler, FormEventHandler } from 'react'

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import * as React from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import noop from 'lib/function/noop'
import TextInput from './TextInput'
import { DMY_DATE_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import HiddenInput from './HiddenInput'

// Checks if the new string is equal to the old string with something added to the end
function didAppend(oldString = '', newString = '') {
  return (
    newString.length > oldString.length &&
    newString.startsWith(oldString)
  )
}

const INVALID_DATE_SIGNAL = 'invalid_date'
const MUST_BE_AFTER_SIGNAL = 'must_be_after'
const MUST_BE_BEFORE_SIGNAL = 'must_be_before'

export type DateOfBirthInvalidHandler = (event: React.FormEvent<HTMLInputElement>, isoDate?: string) => void
interface Props extends Omit<ComponentProps<typeof TextInput>, 'min' | 'max' | 'onChange' | 'pattern'> {
  onChange?: (date: string | undefined, isoDate: string | undefined, event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>, isoDate?: string) => void
  onInvalid?: DateOfBirthInvalidHandler
  getCustomError?: (msg: string) => void
  min?: Date
  max?: Date
  /**
   * in ISO format
   */
  value?: string
  /**
   * in ISO format
   */
  defaultValue?: string
  displayFormat?: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'D MMM YYYY'
}

function getInitialDisplayTextValue(props: Props) {
  const initialValue = props.value || props.defaultValue
  const displayFormat = props.displayFormat ?? DMY_DATE_FORMAT
  if (initialValue) {
    const value = moment(initialValue)
    if (value.isValid()) {
      return value.format(displayFormat)
    }
  }
  return ''
}

const DateInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    min,
    max,
    required,
    placeholder = DMY_DATE_FORMAT,
    displayFormat = DMY_DATE_FORMAT,
    name,
    value,
    defaultValue,
    getInvalidMessage,
    getCustomError,
    onBlur = noop,
    onInvalid = noop,
    onChange = noop,
    ...rest
  } = props

  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => inputRef.current!)

  const [displayText, setDisplayText] = useState<string>(getInitialDisplayTextValue(props))

  const isControlled = value !== undefined

  // when it's an uncontrolled input, we need to manually sync the display text (what the user has input) to the hidden input value
  const actualValue = useMemo(() => {
    if (isControlled) {
      return value
    }

    const possibleValue = moment(displayText, displayFormat, true)
    if (possibleValue.isValid()) {
      return possibleValue.format(ISO_DATE_FORMAT)
    }
  }, [displayFormat, displayText, isControlled, value])

  const invalidMessageGetter = useCallback((input: HTMLInputElement) => {
    let message = getInvalidMessage?.(input)

    if (!message && input.validity.patternMismatch) {
      message = intl.formatMessage(
        {
          id: 'nyG_L-NbGprhZbpzSI81',
          defaultMessage: 'Enter date as {format}',
          description: 'Validation error showing the expected date input format',
        },
        { format: displayFormat },
      )
    }

    if (!message && input.validity.customError) {
      if (input.validationMessage === INVALID_DATE_SIGNAL) {
        message = intl.formatMessage({
          id: '1OPQyap3hQBunZRsZa6G',
          defaultMessage: 'Invalid date',
          description: 'Validation error shown when a date input value is malformed',
        })
      } else if (input.validationMessage.startsWith(`${MUST_BE_AFTER_SIGNAL} `)) {
        message = intl.formatMessage(
          {
            id: 'XfTQQUEXgcjKbcIp3Dfj',
            defaultMessage: 'Must be after {date}',
            description: 'Validation error when date is before the minimum allowed date',
          },
          { date: input.validationMessage.replace(`${MUST_BE_AFTER_SIGNAL} `, '') },
        )
      } else if (input.validationMessage.startsWith(`${MUST_BE_BEFORE_SIGNAL} `)) {
        message = intl.formatMessage(
          {
            id: 'RxvuPdQoKE6kx9FbN5XI',
            defaultMessage: 'Must be before {date}',
            description: 'Validation error when date is after the maximum allowed date',
          },
          { date: input.validationMessage.replace(`${MUST_BE_BEFORE_SIGNAL} `, '') },
        )
      }
    }

    if (message) {
      // Keep the browser validation message localized for native form/reportValidity flows.
      // This means downstream validation that reads `validationMessage` depends on that localized value.
      if (input.validity.customError && input.validationMessage !== message) {
        input.setCustomValidity(message)
      }
      getCustomError?.(message)
    }

    return message
  }, [displayFormat, getCustomError, getInvalidMessage, intl])

  const validate = useCallback((toValidate: string) => {
    if (toValidate.length === 0 && !required) {
      return true
    } else if (toValidate.length !== 10) {
      return false
    }

    const m = moment(toValidate, displayFormat, true)

    if (!inputRef.current) {
      return false
    }

    const setDateValiditySignal = (signal: string) => {
      inputRef.current!.setCustomValidity(signal)
      invalidMessageGetter(inputRef.current!)
      return false
    }

    if (!m.isValid()) {
      // Use stable internal codes here; invalidMessageGetter localizes what users see.
      // User-facing messages are provided by the getInvalidMessage callback.
      return setDateValiditySignal(INVALID_DATE_SIGNAL)
    }

    if (min && m.isBefore(min)) {
      return setDateValiditySignal(`${MUST_BE_AFTER_SIGNAL} ${moment(min).format(displayFormat)}`)
    }

    if (max && m.isAfter(max)) {
      return setDateValiditySignal(`${MUST_BE_BEFORE_SIGNAL} ${moment(max).format(displayFormat)}`)
    }

    inputRef.current.setCustomValidity('')
    return true
  }, [displayFormat, invalidMessageGetter, max, min, required])

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>((event) => {
    const currentValue = event.currentTarget.value
    const isValid = !!currentValue && validate(displayText)
    const isoDate = isValid ? moment(currentValue, displayFormat).format(ISO_DATE_FORMAT) : undefined
    onBlur(event, isoDate)
  }, [displayFormat, displayText, onBlur, validate])

  const handleInvalid = useCallback<FormEventHandler<HTMLInputElement>>((event) => {
    const currentValue = event.currentTarget.value
    const isValid = !!currentValue && validate(displayText)
    const isoDate = isValid ? moment(currentValue, displayFormat).format(ISO_DATE_FORMAT) : undefined
    onInvalid(event, isoDate)
  }, [displayFormat, displayText, onInvalid, validate])

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    const currentValue = event.currentTarget.value
    const isValid = validate(currentValue)

    setDisplayText((currDisplayText) => {
      let nextDisplayText = currentValue

      if (didAppend(currDisplayText, nextDisplayText)) {
        if (nextDisplayText.match(/^\d\d(\/\d\d)?$/)) {
          nextDisplayText = nextDisplayText + '/'
        }

        if (nextDisplayText.match(/\/{2,}/)) {
          nextDisplayText = nextDisplayText.replace(/\/{2,}/g, '/')
        }
      }

      return nextDisplayText.substring(0, 10)
    })

    // empty value is valid, but it's not a date
    if (currentValue && isValid) {
      // legitimate date has been input, notify listeners
      const isoDate = moment(currentValue, displayFormat).format(ISO_DATE_FORMAT)
      onChange(currentValue, isoDate, event)
    } else {
      // not a legitimate date, therefore we have changed to 'empty'
      onChange(undefined, undefined, event)
    }
  }, [displayFormat, onChange, validate])

  useEffect(() => {
    if (value) {
      // values must be of ISO format, if it is - update our display value to match
      const nextValue = moment(value, ISO_DATE_FORMAT, true)
      if (nextValue.isValid()) {
        // legitimate value passed in that was different than before
        // update our input text
        setDisplayText(nextValue.format(displayFormat))
      }
    } else if (value !== undefined) {
      // value was changed to be either null or empty string
      // that means the input should be emptied
      setDisplayText('')
    }
    // value is undefined - it is now (or still is) an uncontrolled input.
    // do nothing with the display text
  }, [value, displayFormat])

  useEffect(() => {
    if (displayText) {
      validate(displayText)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <TextInput
    {...rest}
    ref={inputRef}
    required={required}
    type="text"
    placeholder={placeholder}
    onChange={handleChange}
    onBlur={handleBlur}
    onInvalid={handleInvalid}
    value={displayText}
    pattern="[0-9]{2}\/[0-9]{2}\/[0-9]{4}|[0-9]{1,2}\s[a-zA-Z]{3}\s[0-9]{4}"
    getInvalidMessage={invalidMessageGetter}
  >
    {/*
      Text input displays our "formatted" value which could be different formats
      The hidden input will store the value in a consistent format (ISO Format - YYYY/MM/DD)
    */}
    <HiddenInput
      name={name}
      defaultValue={defaultValue}
      value={actualValue}
    />
  </TextInput>
})

DateInput.displayName = 'DateInput'

export default DateInput
